import { useState, useEffect, useCallback } from 'react';
import { Product, Pagination, ProductFilters } from '@/types';
import { productService, ProductQueryParams } from '@/services/product.service';
import { ApiError } from '@/config/api';
import { products as staticProducts, StaticProduct } from '@/data/products';
import { API_CONFIG } from '@/config/api';

/**
 * Hook state interface
 */
interface UseProductsState {
  products: Product[];
  pagination: Pagination | null;
  filters: ProductFilters | null;
  isLoading: boolean;
  error: string | null;
  refetch: (params?: ProductQueryParams) => Promise<void>;
  loadMore: () => Promise<void>;
  hasMore: boolean;
}

/**
 * Hook options interface
 */
interface UseProductsOptions extends ProductQueryParams {
  autoFetch?: boolean;
}

/**
 * Helper function to get final price of a product
 */
const getFinalPrice = (product: Product): number => {
  return product.f_price || product.special_price || product.price || 0;
};

/**
 * Filter out products with zero or negative price
 */
const filterZeroPriceProducts = (products: Product[]): Product[] => {
  return products.filter(product => {
    const finalPrice = getFinalPrice(product);
    return finalPrice > 0;
  });
};

/**
 * Custom hook for fetching products
 * @param options - Query parameters and hook options
 * @returns Products data, pagination, loading state, error, and utility functions
 */
export function useProducts(options: UseProductsOptions = {}): UseProductsState {
  const { autoFetch = true, ...queryParams } = options;
  
  const [products, setProducts] = useState<Product[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [filters, setFilters] = useState<ProductFilters | null>(null);
  const [isLoading, setIsLoading] = useState(autoFetch);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async (params: ProductQueryParams) => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('fetchProducts called with params:', params);
      const data = await productService.getProducts(params);
      
      // Filter out products with zero price
      console.log('Products fetched from API:', data.products);
      const filteredProducts = filterZeroPriceProducts(data.products);
      
      setProducts(filteredProducts);
      setPagination(data.pagination);
      setFilters(data.filters);
    } catch (err) {
      console.error('Error fetching products from API:', err);
      
      // Fallback to static data
      console.log('Using static product data as fallback');
      
      // Create mock products that match the API structure
      const mockProducts: Product[] = staticProducts.map((staticProduct, index) => ({
        id: index + 1,
        mp_seller_id: 104,
        regular_price: staticProduct.price.toString(),
        sku: `SKU${staticProduct.id}`,
        moq: '1',
        name: staticProduct.name,
        product_number: `PN${staticProduct.id}`,
        url_key: `product-${staticProduct.id}`,
        price: staticProduct.price,
        special_price: staticProduct.price,
        highlights: staticProduct.name,
        product_id: index + 1,
        product_type: 2,
        parent_sku: `parent_${staticProduct.id}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        gst: '18',
        weight: '1.0000',
        category_old_ids: '1,2,3',
        base_image: staticProduct.image,
        default_image: staticProduct.image,
        brand: 1,
        brand_label: staticProduct.brand,
        manufacturer: 1,
        manufacturer_label: staticProduct.brand,
        vendor_company: 'Demo Company',
        vendor_name: 'Demo Vendor',
        vendor_id: '1',
        avg_rating: null,
        total_rating: '0.00',
        attribute_ids: '1',
        attribute_values: '1',
        s3_base_url: '',
        thumb_image: staticProduct.image,
        f_price: staticProduct.price,
        filterable_attributes: {
          brand: '1',
        },
        seller_code: 'DEMO',
        selling_price_discount: 0,
        category_ids: '1,2,3',
        category_names: [staticProduct.category],
        category_ids_new: [1, 2, 3],
        last_category: 3,
        inventories: '100',
        search_keywords: [staticProduct.name, staticProduct.brand],
        groupby_key: staticProduct.name,
        // Legacy fields for backward compatibility
        final_price: staticProduct.price,
        short_description: staticProduct.name,
        description: staticProduct.name,
        status: 1,
        visible_individually: 1,
        meta_title: staticProduct.name,
        meta_keywords: '',
        meta_description: '',
        category_id: 1,
        total_orders: 0,
        category: {
          id: 1,
          name: staticProduct.category,
          slug: 'general',
          url_path: 'general',
          description: '',
          position: 1,
          parent_id: 0,
          image_url: '',
          category_icon_url: '',
          product_count: 0,
        },
        images: [],
        brand_name: staticProduct.brand,
        brand_logo: '',
        media_base_url: '',
        delivery_pincode: '',
        seller_shop_url: '',
        inventory_ids: [],
        postcode: '',
        product_images: [],
      }));
      
      // Filter by category if specified
      let filteredProducts = mockProducts;
      if (params?.category_id && params.category_id !== '') {
        filteredProducts = mockProducts.filter(product => 
          product.category.name.toLowerCase().includes(String(params.category_id).toLowerCase())
        );
      }
      
      // Filter out products with zero price
      filteredProducts = filterZeroPriceProducts(filteredProducts);
      
      // Apply pagination
      const perPage = params?.per_page || 20;
      const page = params?.page || 1;
      const startIndex = (page - 1) * perPage;
      const paginatedProducts = filteredProducts.slice(startIndex, startIndex + perPage);
      
      const total = filteredProducts.length;
      const from = startIndex + 1;
      const to = Math.min(startIndex + perPage, total);
      
      setProducts(paginatedProducts);
      setPagination({
        current_page: page,
        per_page: perPage,
        total: total,
        total_pages: Math.ceil(total / perPage),
        has_next_page: page < Math.ceil(total / perPage),
        has_prev_page: page > 1,
        next_page: page < Math.ceil(total / perPage) ? page + 1 : null,
        prev_page: page > 1 ? page - 1 : null,
        from: from,
        to: to,
      });
      setFilters(null);
      
      // Set error message to indicate fallback
      if (err instanceof ApiError) {
        setError(`API temporarily unavailable. Showing demo products.`);
      } else if (err instanceof Error) {
        setError(`API temporarily unavailable. Showing demo products.`);
      } else {
        setError('API temporarily unavailable. Showing demo products.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!pagination?.has_next_page || isLoading) return;

    try {
      setIsLoading(true);
      setError(null);
      
      const nextPage = pagination.next_page || pagination.current_page + 1;
      const data = await productService.getProducts({
        ...queryParams,
        page: nextPage,
      });
      
      // Filter out products with zero price
      const filteredProducts = filterZeroPriceProducts(data.products);
      
      setProducts(prev => [...prev, ...filteredProducts]);
      setPagination(data.pagination);
      setFilters(data.filters);
    } catch (err) {
      console.error('Error loading more products:', err);
      
      if (err instanceof ApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to load more products');
      }
    } finally {
      setIsLoading(false);
    }
  }, [pagination, isLoading, queryParams]);

  // Refetch when query params change (including on mount if autoFetch)
  useEffect(() => {
    console.log('useProducts effect triggered with params:', queryParams);
    if (autoFetch) {
      fetchProducts(queryParams);
    }
  }, [JSON.stringify(queryParams), autoFetch, fetchProducts]);

  return {
    products,
    pagination,
    filters,
    isLoading,
    error,
    refetch: fetchProducts,
    loadMore,
    hasMore: pagination?.has_next_page || false,
  };
}

/**
 * Hook for searching products
 * @param searchQuery - Search query string
 * @param options - Additional query parameters
 */
export function useProductSearch(
  searchQuery: string,
  options?: Omit<UseProductsOptions, 'search'>
) {
  return useProducts({ ...options, search: searchQuery });
}

/**
 * Hook for fetching products by category
 * @param categoryId - Category ID
 * @param options - Additional query parameters
 */
export function useProductsByCategory(
  categoryId: string | number,
  options?: Omit<UseProductsOptions, 'category_id'>
) {
  return useProducts({ ...options, category_id: categoryId });
}
